import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
  Avatar,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  RestaurantMenu,
  Logout,
  ChevronLeft,
  SupervisorAccount,
  Category,
  Kitchen,
  ReceiptLong,
  DataUsage,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { sectionIcons } from '../../assets/icons';
import '../../styles/index.css';

// Icono de sección monocromo: se tiñe con el color del enlace (currentColor) mediante mask,
// así adopta el gris en inactivo y el blanco cuando el ítem está activo.
const SectionImgIcon = ({ src }) => (
  <Box
    component="span"
    sx={{
      width: 32,
      height: 32,
      display: 'block',
      bgcolor: 'currentColor',
      WebkitMaskImage: `url(${src})`,
      maskImage: `url(${src})`,
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      maskPosition: 'center',
      WebkitMaskSize: 'contain',
      maskSize: 'contain',
    }}
  />
);

const Sidebar = ({ isCollapsed, onToggle }) => {
  const theme = useTheme();
  const { logout, user, userRole } = useAuth();
  const navigate = useNavigate();
  // We use props for collapse state to control it from parent if needed, 
  // but for now purely internal control or layout control is better. 
  // Let's rely on props so MainLayout can adjust margin.

  const menuItems = [
    { text: 'Panel Principal', icon: <SectionImgIcon src={sectionIcons.dashboard} />, path: '/dashboard' },
    { text: 'Plan semanal', icon: <SectionImgIcon src={sectionIcons.planSemanal} />, path: '/menu-semanal' },
    // "Análisis Corporal" removed as requested
    { text: 'Recetas', icon: <SectionImgIcon src={sectionIcons.recetas} />, path: '/recetas' },
    { text: 'Lista de Compra', icon: <SectionImgIcon src={sectionIcons.lists} />, path: '/listas' },
    { text: 'Configuración', icon: <SectionImgIcon src={sectionIcons.user} />, path: '/perfil' },
  ];

  if (userRole === 'admin') {
    menuItems.push(
      { text: 'Gestión Recetas', icon: <ReceiptLong />, path: '/gestion-recetas' },
      { text: 'Gestión Ingredientes', icon: <Kitchen />, path: '/gestion-ingredientes' },
      { text: 'Taxonomías', icon: <Category />, path: '/gestion-taxonomias' },
      { text: 'Consumo IA', icon: <DataUsage />, path: '/consumo-ia' },
      { text: 'Usuarios', icon: <SupervisorAccount />, path: '/gestion-usuarios' }
    );
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Box
      sx={{
        width: isCollapsed ? 88 : 280,
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bgcolor: '#ffffff',
        borderRight: '1px solid #f0f0f0',
        display: 'flex',
        flexDirection: 'column',
        padding: isCollapsed ? 2 : 3,
        zIndex: 1200,
        transition: 'width 0.3s ease',
        overflowX: 'hidden'
      }}
    >
      {/* Brand Logo & Toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 5, justifyContent: isCollapsed ? 'center' : 'space-between', flexDirection: isCollapsed ? 'column' : 'row', gap: isCollapsed ? 2 : 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              bgcolor: '#E0E7FF',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#667eea',
              flexShrink: 0,
              cursor: isCollapsed ? 'pointer' : 'default'
            }}
            onClick={isCollapsed ? onToggle : undefined}
          >
            <RestaurantMenu fontSize="small" />
          </Box>
          {!isCollapsed && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1, whiteSpace: 'nowrap' }}>
                Nutrizia
              </Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>
                Seguimiento Integral
              </Typography>
            </Box>
          )}
        </Box>

        {/* Toggle Button */}
        <IconButton onClick={onToggle} size="small" sx={{ color: '#94a3b8', transform: isCollapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }}>
          <ChevronLeft />
        </IconButton>
      </Box>

      {/* Navigation Menu */}
      <List sx={{ flex: 1 }}>
        {menuItems.map((item) => (
          <ListItem
            key={item.text}
            disablePadding
            sx={{ mb: 1, justifyContent: isCollapsed ? 'center' : 'initial' }}
          >
            <Tooltip title={isCollapsed ? item.text : ""} placement="right">
              <NavLink
                to={item.path}
                style={({ isActive }) => ({
                  textDecoration: 'none',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  backgroundColor: isActive ? '#667eea' : 'transparent',
                  color: isActive ? '#ffffff' : '#64748b',
                  fontWeight: isActive ? 600 : 500,
                  transition: 'all 0.2s ease',
                })}
              >
                <ListItemIcon
                  sx={{
                    minWidth: isCollapsed ? 0 : 40,
                    color: 'inherit',
                    justifyContent: 'center'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.95rem',
                      fontWeight: 'inherit',
                      whiteSpace: 'nowrap'
                    }}
                  />
                )}
              </NavLink>
            </Tooltip>
          </ListItem>
        ))}
      </List>

      {/* User Profile / Logout */}
      <Box
        sx={{
          mt: 'auto',
          p: isCollapsed ? 1 : 2,
          bgcolor: isCollapsed ? 'transparent' : '#f8fafc',
          borderRadius: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1.5, justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
          <Avatar
            src={user?.photoURL || "https://i.pravatar.cc/150?u=a042581f4e29026704d"}
            alt="User"
            sx={{ width: 40, height: 40 }}
          />
          {!isCollapsed && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b', lineHeight: 1.2 }}>
                {user?.displayName || 'Usuario'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#667eea', fontWeight: 500 }}>
                Pro Plan
              </Typography>
            </Box>
          )}
        </Box>

        {!isCollapsed ? (
          <Button
            variant="outlined"
            fullWidth
            onClick={handleLogout}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              fontSize: '0.85rem',
              borderColor: '#e2e8f0',
              color: '#64748b',
              '&:hover': {
                borderColor: '#cbd5e1',
                bgcolor: '#ffffff'
              }
            }}
          >
            Cerrar Sesión
          </Button>
        ) : (
          <IconButton onClick={handleLogout} color="error">
            <Logout />
          </IconButton>
        )}
      </Box>

      {/* Toggle Button for Mobile/Collapsed State when collapsed and not shown in header */}

    </Box>
  );
};

export default Sidebar;
